"use client";
import { useEffect, useRef, useState, useCallback } from "react";
import type {
  FurnitureAgentEvent,
  FurnitureAgentJob,
  FurnitureAgentStep,
} from "@/types/furniture-agent";
import { STEP_ORDER, STEP_META } from "@/lib/furniture-agent/steps";

/** 비어있는 초기 스텝 배열 */
const seedSteps = (): FurnitureAgentStep[] =>
  STEP_ORDER.map((id) => ({ id, label: STEP_META[id].label, status: "pending" }));

export function useJobStream(jobId: string | null) {
  const [job, setJob] = useState<FurnitureAgentJob | null>(null);
  const [actionLog, setActionLog] = useState<string[]>([]); // 최신이 앞
  const esRef = useRef<EventSource | null>(null);

  const pushLog = useCallback((msg: string) => {
    setActionLog((prev) => [msg, ...prev].slice(0, 20));
  }, []);

  useEffect(() => {
    if (!jobId) return;

    setJob({
      jobId,
      displayName: "견적 자동 분석",
      backendType: "SCHEDULE-AUTO-BRIEFING",
      status: "queued",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      steps: seedSteps(),
      overallProgress: 0,
    });

    const es = new EventSource(`/api/furniture-agent/events?jobId=${encodeURIComponent(jobId)}`);
    esRef.current = es;

    const handle = (raw: MessageEvent) => {
      try {
        const ev = JSON.parse(raw.data) as FurnitureAgentEvent;
        setJob((prev) => (prev ? reduce(prev, ev) : prev));
        if (ev.type === "action.log") pushLog(ev.payload.message);
        if (ev.type === "step.transition" && ev.payload.message) pushLog(ev.payload.message);
      } catch {
        /* ignore */
      }
    };

    es.addEventListener("job.created", handle as any);
    es.addEventListener("step.transition", handle as any);
    es.addEventListener("action.log", handle as any);
    es.addEventListener("job.finished", handle as any);

    es.onerror = () => {
      // 브라우저가 자동 재연결함. 필요 시 지수 백오프 직접 구현.
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [jobId, pushLog]);

  return { job, actionLog };
}

/** 이벤트 → Job 상태 reducer */
function reduce(job: FurnitureAgentJob, ev: FurnitureAgentEvent): FurnitureAgentJob {
  switch (ev.type) {
    case "job.created":
      return { ...ev.payload };
    case "step.transition": {
      const { stepId, to, progress, latencyMs, message } = ev.payload;
      const steps = job.steps.map((s) =>
        s.id === stepId
          ? {
              ...s,
              status: to as any,
              progress,
              latencyMs,
              message: message ?? s.message,
              startedAt: to === "running" ? new Date().toISOString() : s.startedAt,
              endedAt:
                to === "success" || to === "failed" || to === "skipped"
                  ? new Date().toISOString()
                  : s.endedAt,
            }
          : s,
      );
      const done = steps.filter((s) => s.status === "success" || s.status === "skipped").length;
      return {
        ...job,
        steps,
        currentStepId: to === "running" ? stepId : job.currentStepId,
        overallProgress: done / steps.length,
        status: job.status === "queued" ? "running" : job.status,
        updatedAt: ev.ts,
      };
    }
    case "action.log": {
      const steps = job.steps.map((s) =>
        s.id === ev.payload.stepId ? { ...s, message: ev.payload.message } : s,
      );
      return { ...job, steps, updatedAt: ev.ts };
    }
    case "job.finished":
      return { ...job, status: ev.payload.status, updatedAt: ev.ts };
    default:
      return job;
  }
}
